import type {
  SideNavItem,
  SideNavLink,
  SideNavRegionAttributes,
} from '@evisort/eds/components/AppLayout/subcomponents/SideNav/types';
import type { IconType } from '@evisort/eds/types/SharedIconProps';

import { testIsActiveLink } from '~/eds/src/modules/PageHeader/utils';
import { NavLink } from '~/eds/src/types/NavLink';

import { getSidebarAnalyticsItem } from './sidebarAnalytics';

export const ASK_AI_KEY = '/ask-ai';
export const FEEDBACK_KEY = '/give-feedback';

const NAV_ICON_BY_BASE_ICON: Partial<Record<IconType, IconType>> = {
  'ai-custom-automation': 'nav-automation',
  dashboard: 'nav-dashboard',
  document: 'nav-documents',
  home: 'nav-home',
  library: 'nav-library',
  search: 'nav-search',
  'settings-gear': 'nav-admin',
  workflow: 'nav-workflow',
};

function getBaseIcon(pathname: string): IconType {
  const path = pathname.toLowerCase();
  if (
    path.startsWith('/automation') ||
    path.startsWith('/custom-ai') ||
    path.startsWith('/x-ray/library')
  ) {
    return 'ai-custom-automation';
  }
  if (path.startsWith('/admin')) return 'settings-gear';
  if (path.startsWith('/alerts')) return 'alert';
  if (
    path.startsWith('/clause-library') ||
    path.startsWith('/guidelines') ||
    path.startsWith('/playbooks')
  ) {
    return 'library';
  }
  if (path.startsWith('/dashboard')) return 'dashboard';
  if (path.startsWith('/document')) return 'document';
  if (path.startsWith('/managed-entities') || path.startsWith('/parties')) {
    return 'work-briefcase';
  }
  if (path.startsWith('/search')) return 'search';
  if (path.startsWith('/saved-searches')) return 'bookmark';
  if (path.startsWith('/upload')) return 'upload';
  if (path.startsWith('/workflow')) return 'workflow';
  return 'document';
}

export function getNavIcon(pathname: string): IconType {
  const baseIcon = getBaseIcon(pathname);
  return NAV_ICON_BY_BASE_ICON[baseIcon] ?? baseIcon;
}

export function toSideNavLink(
  link: NavLink,
  { isAction = false }: { isAction?: boolean } = {},
): SideNavLink {
  return {
    badge: link.badge,
    dataAttributes: {
      'data-amp-track-item': getSidebarAnalyticsItem(link.pathname),
    },
    href: isAction ? undefined : link.pathname,
    key: link.pathname,
    label: link.label,
  };
}

interface SideNavItemOptions {
  /** Analytics section the entry reports under, when it is not part of a group. */
  analyticsSection?: string;
  /** Overrides the icon derived from the link's pathname. */
  icon?: IconType;
  iconVariant?: SideNavItem['iconVariant'];
  /** Marks an entry that acts, such as opening a panel, instead of navigating. */
  isAction?: boolean;
  testId?: string;
}

export function toSideNavItem(
  link: NavLink,
  {
    analyticsSection,
    icon,
    iconVariant,
    isAction = false,
    testId,
  }: SideNavItemOptions = {},
): SideNavItem {
  const analyticsItem = getSidebarAnalyticsItem(link.pathname);
  const children = link.children?.map((child) => toSideNavLink(child));
  const hasChildren = Boolean(children?.length);

  return {
    ...toSideNavLink(link, { isAction }),
    children,
    dataAttributes: {
      'data-amp-track-item': analyticsItem,
      ...(!hasChildren && analyticsSection
        ? { 'data-amp-track-section': analyticsSection }
        : {}),
      ...(testId ? { 'data-testid': testId } : {}),
    },
    groupDataAttributes: hasChildren
      ? { 'data-amp-track-section': analyticsItem }
      : undefined,
    icon: icon ?? getNavIcon(link.pathname),
    iconVariant,
    toggleDataAttributes: {
      'data-amp-track-action': 'toggle-section',
      'data-amp-track-item': analyticsItem,
    },
  };
}

/**
 * The key of the most specific entry the current page belongs to: a nested link
 * when one matches, otherwise the section itself. The first match wins, so a
 * page lights up one place in the navigation.
 */
export function resolveActiveKey(pathname: string, links: NavLink[]) {
  const isActive = testIsActiveLink(pathname);

  for (const link of links) {
    const activeChild = link.children?.find(isActive);
    if (activeChild) return activeChild.pathname;
    if (isActive(link)) return link.pathname;
  }
  return null;
}

/**
 * Applies a new order of the visible pinned entries to the stored order. A pin
 * whose entry is not visible, such as after a permission change, keeps its
 * place so it comes back in the same spot if the entry does.
 */
export function applyPinnedOrder(
  storedPathnames: string[],
  orderedVisiblePathnames: string[],
) {
  const visible = new Set(orderedVisiblePathnames);
  const queue = [...orderedVisiblePathnames];

  return storedPathnames.map((pathname) =>
    visible.has(pathname) ? (queue.shift() ?? pathname) : pathname,
  );
}

export function getRegionAttributes(
  isCollapsed: boolean,
): SideNavRegionAttributes {
  const root = {
    'data-amp-track-region': 'nav',
    'data-amp-track-variant': isCollapsed ? 'collapsed' : 'open',
  };

  return {
    collapseToggle: {
      'data-amp-track-item': 'sidebar-visibility-toggle',
      'data-testid': 'sidebar-panel-toggle',
    },
    drawerClose: {
      'data-amp-track-item': 'sidebar-visibility-toggle',
      'data-testid': 'sidebar-mobile-close',
    },
    drawerHeader: {
      'data-amp-track-section': 'header',
      'data-testid': 'sidebar-mobile-header',
    },
    flyout: { ...root, 'data-amp-track-section': 'flyout-navigation' },
    header: {
      'data-amp-track-section': 'sidebar',
      'data-testid': 'sidebar-workspace-header',
    },
    navigation: { 'data-amp-track-section': 'primary-nav' },
    pin: { 'data-amp-track-action': 'pin-nav-item' },
    pinned: { 'data-amp-track-section': 'pinned-nav' },
    root,
    sectionToggle: { 'data-amp-track-action': 'toggle-section' },
    unpin: { 'data-amp-track-action': 'unpin-nav-item' },
  };
}
